import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { useTranslation } from "react-i18next";
import { FaArrowLeft, FaUser } from "react-icons/fa";
import { getUserId } from "../utils/userUtils";
import { createCharacter, uploadCharacterImage } from "../utils/characterUtils";
import ImageUpload from "../components/ImageUpload";
import axios from "axios";

const CreateCharacter = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthenticator((context) => [context.user]);
  
  const [creatingCharacter, setCreatingCharacter] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  const [notification, setNotification] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    personality_prompt: "",
    system_prompt: "",
    is_public: false,
    status_emoji: "🔥",
    short_description: "",
    creator_name: ""
  });
  const [errors, setErrors] = useState({});
  const [avatarImage, setAvatarImage] = useState(null);
  const [selfieImages, setSelfieImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);

  // Available status emojis for selection
  const statusEmojis = ["🔥", "🧪", "⭐", "💫", "🚀", "🎭", "🤖", "💎"];

  // Helper function to show notifications
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  // Check if user has an active subscription
  const checkSubscription = async () => {
    if (!user) {
      setIsSubscribed(false);
      setCheckingSubscription(false);
      return;
    }

    try {
      const userId = getUserId(user);
      if (!userId) {
        setIsSubscribed(false);
        setCheckingSubscription(false);
        return;
      }

      const response = await axios.get(
        `https://d4gscm5o97.execute-api.us-east-1.amazonaws.com/prod/subscriptions/${userId}`
      );

      const subscription = response.data;
      setIsSubscribed(subscription.subscription_status === "active");
    } catch (error) {
      console.error("Error checking subscription:", error);
      setIsSubscribed(false);
    } finally {
      setCheckingSubscription(false);
    }
  };

  // Form handling functions
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = "Character name is required";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Character name must be at least 2 characters";
    }
    
    if (!formData.description.trim()) {
      newErrors.description = "Character description is required";
    } else if (formData.description.trim().length < 10) {
      newErrors.description = "Description must be at least 10 characters";
    }
    
    if (!formData.personality_prompt.trim()) {
      newErrors.personality_prompt = "Personality description is required";
    } else if (formData.personality_prompt.trim().length < 20) {
      newErrors.personality_prompt = "Personality description must be at least 20 characters";
    }

    // Validate publication fields if character is being published
    if (formData.is_public) {
      if (!formData.short_description.trim()) {
        newErrors.short_description = "Short description is required for published characters";
      } else if (formData.short_description.trim().length < 10) {
        newErrors.short_description = "Short description must be at least 10 characters";
      } else if (formData.short_description.trim().length > 150) {
        newErrors.short_description = "Short description must be less than 150 characters";
      }
      
      if (!formData.creator_name.trim()) {
        newErrors.creator_name = "Creator name is required for published characters";
      } else if (formData.creator_name.trim().length < 2) {
        newErrors.creator_name = "Creator name must be at least 2 characters";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle character creation
  const handleCreateCharacter = async (e) => {
    e.preventDefault();
    
    if (!user || !isSubscribed) return;
    if (!validateForm()) return;

    setCreatingCharacter(true);
    try {
      const userId = getUserId(user);
      if (!userId) {
        throw new Error("User ID not available");
      }
      
      // Prepare character data
      const characterData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        personality_prompt: formData.personality_prompt.trim(),
        system_prompt: formData.system_prompt.trim() || "",
        base_character: "sam",
        is_public: formData.is_public,
        status_emoji: formData.status_emoji,
        short_description: formData.short_description.trim(),
        creator_name: formData.creator_name.trim()
      };
      
      const result = await createCharacter(characterData, userId);
      if (result && result.character_id) {
        // Handle image uploads if present
        if (avatarImage || selfieImages.length > 0) {
          await handleImageUploads(result.character_id, userId);
        }
        
        showNotification("Character created successfully!", "success");
        // Navigate back to control panel after a short delay
        setTimeout(() => {
          navigate("/controlpanel");
        }, 2000);
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      console.error("Error creating character:", error);
      const errorMessage = error.message.includes("Failed to create character") 
        ? error.message 
        : "Failed to create character. Please check your connection and try again.";
      showNotification(errorMessage, "error");
    } finally {
      setCreatingCharacter(false);
    }
  };

  const handleImageUploads = async (characterId, userId) => {
    setUploadingImages(true);
    
    try {
      // Upload avatar if present
      if (avatarImage) {
        await uploadCharacterImage(characterId, userId, avatarImage, 'avatar');
      }
      
      // Upload selfies if present
      for (const selfieImage of selfieImages) {
        await uploadCharacterImage(characterId, userId, selfieImage, 'selfie');
      }
      
      console.log('All images uploaded successfully');
    } catch (error) {
      console.error('Error uploading images:', error);
    } finally {
      setUploadingImages(false);
    }
  };

  // Handle cancel - go back to previous page
  const handleCancel = () => {
    navigate(-1);
  };

  // Check subscription when component mounts
  useEffect(() => {
    checkSubscription();
  }, [user]);

  // Redirect if not logged in
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center pt-24">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Please sign in to create characters</h1>
          <button
            onClick={() => navigate("/controlpanel")}
            className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg transition-colors"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  // Show subscription requirement if not subscribed
  if (!checkingSubscription && !isSubscribed) {
    return (
      <div className="min-h-screen bg-gray-900 text-white pt-24">
        <div className="max-w-4xl mx-auto p-6">
          <div className="flex items-center gap-4 mb-8">
            <button
              onClick={handleCancel}
              className="text-white hover:text-gray-300 transition-colors"
            >
              <FaArrowLeft size={24} />
            </button>
            <h1 className="text-3xl font-bold">Create Character</h1>
          </div>

          <div className="bg-gray-800 rounded-xl p-8 text-center">
            <FaUser className="mx-auto text-6xl text-gray-600 mb-6" />
            <h2 className="text-2xl font-bold mb-4">Subscription Required</h2>
            <p className="text-gray-300 mb-6 max-w-md mx-auto">
              Creating custom characters is a premium feature. Subscribe to unlock unlimited character creation and access to advanced AI personalities.
            </p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => navigate("/subscription-management")}
                className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg transition-colors"
              >
                View Subscription Plans
              </button>
              <button
                onClick={handleCancel}
                className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg transition-colors"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state
  if (checkingSubscription) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center pt-24">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-500 mx-auto"></div>
          <p className="mt-4 text-gray-400">Checking subscription status...</p>
        </div>
      </div>
    );
  }

  // Main create character view
  return (
    <div className="min-h-screen bg-gray-900 text-white pt-24">
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={handleCancel}
            className="text-white hover:text-gray-300 transition-colors"
          >
            <FaArrowLeft size={24} />
          </button>
          <h1 className="text-3xl font-bold">Create New Character</h1>
        </div>

        <div className="bg-gray-800 rounded-xl p-8">
          <form onSubmit={handleCreateCharacter} className="space-y-6">
            {/* Character Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                Character Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 bg-gray-700 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.name ? "border-red-500" : "border-gray-600"
                }`}
                placeholder="Enter character name"
                disabled={creatingCharacter}
              />
              {errors.name && <p className="text-red-400 text-sm mt-1">{errors.name}</p>}
            </div>

            {/* Character Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-300 mb-2">
                Character Description *
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className={`w-full px-3 py-2 bg-gray-700 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical ${
                  errors.description ? "border-red-500" : "border-gray-600"
                }`}
                placeholder="Describe your character's role and purpose"
                disabled={creatingCharacter}
              />
              {errors.description && <p className="text-red-400 text-sm mt-1">{errors.description}</p>}
            </div>

            {/* Personality Prompt */}
            <div>
              <label htmlFor="personality_prompt" className="block text-sm font-medium text-gray-300 mb-2">
                Personality & Behavior *
              </label>
              <textarea
                id="personality_prompt"
                name="personality_prompt"
                value={formData.personality_prompt}
                onChange={handleInputChange}
                rows={4}
                className={`w-full px-3 py-2 bg-gray-700 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical ${
                  errors.personality_prompt ? "border-red-500" : "border-gray-600"
                }`}
                placeholder="Describe how your character should behave, their personality traits, communication style, etc."
                disabled={creatingCharacter}
              />
              {errors.personality_prompt && <p className="text-red-400 text-sm mt-1">{errors.personality_prompt}</p>}
            </div>

            {/* System Prompt (Optional) */}
            <div>
              <label htmlFor="system_prompt" className="block text-sm font-medium text-gray-300 mb-2">
                Advanced Instructions (Optional)
              </label>
              <textarea
                id="system_prompt"
                name="system_prompt"
                value={formData.system_prompt}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical"
                placeholder="Additional system instructions or constraints (optional)"
                disabled={creatingCharacter}
              />
            </div>

            {/* Character Visibility Section */}
            <div className="border-t border-gray-600 pt-6">
              <h3 className="text-lg font-medium text-white mb-4">Character Visibility</h3>
              
              {/* Publish to Character Board Toggle */}
              <div className="mb-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_public"
                    checked={formData.is_public}
                    onChange={handleInputChange}
                    disabled={creatingCharacter}
                    className="sr-only"
                  />
                  <div className={`relative w-11 h-6 rounded-full transition-colors duration-200 ease-in-out ${
                    formData.is_public ? 'bg-blue-600' : 'bg-gray-600'
                  }`}>
                    <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-200 ease-in-out ${
                      formData.is_public ? 'transform translate-x-5' : ''
                    }`}></div>
                  </div>
                  <span className="ml-3 text-sm text-gray-300">
                    Publish to Character Board
                  </span>
                </label>
                <p className="text-gray-400 text-xs mt-1 ml-14">
                  Make this character discoverable by all users on the public character board
                </p>
              </div>

              {/* Publication Fields - Only show when character is set to public */}
              {formData.is_public && (
                <div className="space-y-4 ml-4 border-l-2 border-blue-600 pl-4">
                  {/* Status Emoji Selector */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Status Emoji
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {statusEmojis.map((emoji) => (
                        <button
                          key={emoji}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, status_emoji: emoji }))}
                          className={`w-10 h-10 text-xl rounded-lg border-2 transition-all duration-200 flex items-center justify-center ${
                            formData.status_emoji === emoji
                              ? 'border-blue-500 bg-blue-900 bg-opacity-30'
                              : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                          }`}
                          disabled={creatingCharacter}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                    <p className="text-gray-400 text-xs mt-1">
                      Choose an emoji to represent your character's status or theme
                    </p>
                  </div>

                  {/* Short Description */}
                  <div>
                    <label htmlFor="short_description" className="block text-sm font-medium text-gray-300 mb-2">
                      Short Description for Character Board *
                    </label>
                    <textarea
                      id="short_description"
                      name="short_description"
                      value={formData.short_description}
                      onChange={handleInputChange}
                      rows={2}
                      maxLength={150}
                      className={`w-full px-3 py-2 bg-gray-700 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical ${
                        errors.short_description ? "border-red-500" : "border-gray-600"
                      }`}
                      placeholder="Brief description for the character board (max 150 characters)"
                      disabled={creatingCharacter}
                    />
                    <div className="flex justify-between items-center mt-1">
                      {errors.short_description && <p className="text-red-400 text-xs">{errors.short_description}</p>}
                      <p className="text-gray-400 text-xs ml-auto">
                        {formData.short_description.length}/150
                      </p>
                    </div>
                  </div>

                  {/* Creator Name */}
                  <div>
                    <label htmlFor="creator_name" className="block text-sm font-medium text-gray-300 mb-2">
                      Creator Name *
                    </label>
                    <input
                      type="text"
                      id="creator_name"
                      name="creator_name"
                      value={formData.creator_name}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 bg-gray-700 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.creator_name ? "border-red-500" : "border-gray-600"
                      }`}
                      placeholder="Your name or display name as the creator"
                      disabled={creatingCharacter}
                    />
                    {errors.creator_name && <p className="text-red-400 text-xs mt-1">{errors.creator_name}</p>}
                  </div>

                  {/* Character Board Preview */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Character Board Preview
                    </label>
                    <div className="bg-gray-700 border border-gray-600 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <div className="w-12 h-12 rounded-full bg-gray-600 flex items-center justify-center overflow-hidden">
                          {avatarImage ? (
                            <img 
                              src={avatarImage.preview} 
                              alt="Character preview" 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-gray-400 text-xs">No Image</span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <h4 className="text-white font-medium truncate">
                              {formData.name || 'Character Name'}
                            </h4>
                            <span className="text-lg">{formData.status_emoji}</span>
                            <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded">
                              Community
                            </span>
                          </div>
                          <p className="text-gray-300 text-sm mb-2 line-clamp-2">
                            {formData.short_description || 'Short description will appear here...'}
                          </p>
                          <p className="text-gray-400 text-xs">
                            by {formData.creator_name || 'Creator Name'}
                          </p>
                        </div>
                      </div>
                    </div>
                    <p className="text-gray-400 text-xs mt-1">
                      This is how your character will appear on the Character Discovery Board
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Character Images Section */}
            <div className="border-t border-gray-600 pt-6">
              <h3 className="text-lg font-medium text-white mb-4">Character Images</h3>
            
              {/* Character Avatar Upload */}
              <ImageUpload
                type="avatar"
                onImageSelect={setAvatarImage}
                maxSize={1}
                disabled={creatingCharacter || uploadingImages}
              />

              {/* Character Selfie Upload */}
              <ImageUpload
                type="selfie"
                onImageSelect={(imageData) => {
                  if (imageData) {
                    setSelfieImages(prev => [...prev, imageData]);
                  }
                }}
                maxSize={5}
                disabled={creatingCharacter || uploadingImages}
              />

              {/* Show selfie images */}
              {selfieImages.length > 0 && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-300">
                    Uploaded Selfies ({selfieImages.length})
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {selfieImages.map((image, index) => (
                      <div key={index} className="relative">
                        <img
                          src={image.preview}
                          alt={`Selfie ${index + 1}`}
                          className="w-full h-20 object-cover rounded"
                        />
                        <button
                          type="button"
                          onClick={() => setSelfieImages(prev => prev.filter((_, i) => i !== index))}
                          className="absolute top-1 right-1 bg-red-600 hover:bg-red-700 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                          disabled={creatingCharacter || uploadingImages}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleCancel}
                className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={creatingCharacter}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                disabled={creatingCharacter || uploadingImages}
              >
                {creatingCharacter ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Creating...
                  </>
                ) : uploadingImages ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Uploading Images...
                  </>
                ) : (
                  "Create Character"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Notification Toast */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 max-w-sm p-4 rounded-lg shadow-lg transform transition-all duration-300 ${
          notification.type === 'success' 
            ? 'bg-green-600 text-white border border-green-500' 
            : 'bg-red-600 text-white border border-red-500'
        }`}>
          <div className="flex items-start">
            <div className="flex-shrink-0 mr-3">
              {notification.type === 'success' ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium">{notification.message}</p>
            </div>
            <button
              onClick={() => setNotification(null)}
              className="flex-shrink-0 ml-3 text-white hover:text-gray-200 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateCharacter;